import React from "react";
import { Link } from "react-router-dom";
import SiteFooter from "../components/SiteFooter";
import SiteTopNav from "../components/SiteTopNav";

type InfoPageShellProps = {
  eyebrow: string;
  title: string;
  intro: string;
  children: React.ReactNode;
};

function InfoPageShell({ eyebrow, title, intro, children }: InfoPageShellProps) {
  return (
    <div className="min-h-screen bg-[var(--ng-bg)] text-[var(--ng-text)]">
      <SiteTopNav />
      <main className="px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
        <article className="mx-auto max-w-5xl">
          <header className="border-2 border-[var(--ng-border)] bg-white p-6 sm:p-10">
            <div className="ng-eyebrow">{eyebrow}</div>
            <h1 className="mt-5 max-w-4xl text-5xl font-black uppercase leading-[0.9] tracking-[-0.06em] text-[var(--ng-primary)] sm:text-7xl">
              {title}
            </h1>
            <p className="mt-6 max-w-3xl text-lg font-medium leading-8 text-[var(--ng-text-muted)]">
              {intro}
            </p>
          </header>

          <div className="grid gap-6 py-6 [&_a]:font-bold [&_a]:underline [&_h2]:text-2xl [&_h2]:font-black [&_h2]:uppercase [&_h2]:tracking-[-0.03em] [&_li]:leading-7 [&_p]:leading-7">
            {children}
          </div>
        </article>
      </main>
      <SiteFooter />
    </div>
  );
}

function InfoSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4 border-2 border-[var(--ng-border)] bg-white p-6 sm:p-8">
      <h2>{title}</h2>
      <div className="space-y-4 text-base font-medium text-[var(--ng-text-muted)]">
        {children}
      </div>
    </section>
  );
}

export function AboutPage() {
  return (
    <InfoPageShell
      eyebrow="About / Product and editorial standards"
      title="Useful tools, accurate claims, no signup gate."
      intro="No Gatekeeping is a focused collection of browser utilities and first-party workflow notes. The goal is simple: let a person complete the task described on the page, understand the tool's limits, and leave with a result they can use."
    >
      <InfoSection title="What the site publishes">
        <p>
          The main product is a directory of working image, text, design, URL,
          and SEO utilities. Each tool page pairs the interface with practical
          instructions, use cases, limitations, and a plain-language data
          handling note based on the behavior of that specific tool.
        </p>
        <p>
          The blog is separate editorial material. A card is only presented as
          a published article when a complete article exists at its own URL.
          Shorter ideas that lead directly to a tool are labeled as workflow
          starters instead of being dressed up as articles.
        </p>
      </InfoSection>

      <InfoSection title="How pages are reviewed">
        <ul className="grid gap-3 pl-5 [list-style:disc]">
          <li>Product claims are checked against the working interface and supported formats.</li>
          <li>Tool pages disclose whether work stays in the browser, uses local storage, calls a No Gatekeeping endpoint, or relies on a third-party service.</li>
          <li>Metadata, canonical URLs, headings, structured data, internal links, sitemap coverage, and rendered content are checked across every indexable route during the site build.</li>
          <li>Visible FAQ answers and FAQ structured data come from the same source so they cannot quietly drift apart.</li>
          <li>Articles are first-party perspective and analysis; they are not purchased placements or republished merchant copy.</li>
        </ul>
      </InfoSection>

      <InfoSection title="What No Gatekeeping does not promise">
        <p>
          A free browser tool cannot guarantee that every source file, remote
          website, DNS resolver, speech service, or browser will behave the same
          way. Pages call out important format limits and network dependencies,
          and results that can contain uncertainty—such as OCR, DNS checks, or
          redirect traces—should be reviewed before they drive a production
          decision.
        </p>
        <p>
          The site does not sell rankings, links, reviews, or access to the
          tools. It does not publish third-party pages to borrow the site's
          reputation, and it does not create near-duplicate location or keyword
          pages that funnel into the same utility.
        </p>
      </InfoSection>

      <InfoSection title="Ownership, source, and feedback">
        <p>
          No Gatekeeping is responsible for the tool pages and editorial
          material published here. The project source and change history are
          available in the public repository, which is also the best place to
          report a reproducible product or content issue.
        </p>
        <p>
          <a
            href="https://github.com/semifrontier/ng-app-new"
            rel="noreferrer"
            target="_blank"
          >
            View the No Gatekeeping source repository
          </a>
          . For details about analytics, local storage, uploaded files, and
          network tools, read the <Link to="/privacy">privacy and data handling page</Link>.
        </p>
      </InfoSection>
    </InfoPageShell>
  );
}

export function PrivacyPage() {
  return (
    <InfoPageShell
      eyebrow="Privacy / Data handling"
      title="What stays local and what uses the network."
      intro="No Gatekeeping does not require an account. Different tools handle data differently, so every tool page includes its own disclosure; this page explains the site-wide services and the major processing categories."
    >
      <InfoSection title="Site analytics and assets">
        <p>
          On nogatekeeping.com, the site loads Google Analytics 4 to record page
          views. That request can provide Google with the page URL and standard
          browser, device, network, and event information, and Google Analytics
          may use browser storage or cookies. Analytics is disabled on ordinary
          local-development hosts unless it is explicitly enabled.
        </p>
        <p>
          The interface loads the Syne font from Google Fonts. The image-to-text
          tool loads Tesseract OCR code and language resources from its
          distribution network when OCR is used. Those providers receive the
          normal request information needed to deliver their files.
        </p>
      </InfoSection>

      <InfoSection title="Tools that process in the browser">
        <p>
          Case conversion, proportion calculations, lorem ipsum generation,
          color sampling, image compression, supported image conversion, font
          conversion, wireframe assembly, and heatmap prototyping run in the
          browser. No Gatekeeping does not upload those source files to a
          processing endpoint.
        </p>
        <p>
          Image-to-text OCR also runs in the browser after the OCR library and
          language resources load. Color palette favorites, campaign URL
          history, and Vector Vault additions use localStorage on the current
          device. Clearing this site's browser storage removes those saved
          items.
        </p>
      </InfoSection>

      <InfoSection title="Tools that use a server or third party">
        <ul className="grid gap-3 pl-5 [list-style:disc]">
          <li><strong>DNS Checker:</strong> sends the entered domain, record type, and resolver choice to a No Gatekeeping endpoint, which queries the selected public DNS resolver.</li>
          <li><strong>Redirect Analyzer:</strong> sends the entered public URL to a No Gatekeeping endpoint, which requests the URL and follows its redirect responses.</li>
          <li><strong>Image Extractor:</strong> tries to request the public page from the browser and uses AllOrigins or corsproxy.io as fallbacks when cross-origin rules block the request.</li>
          <li><strong>Sitemap Robots Explorer:</strong> sends public robots.txt and sitemap URLs through AllOrigins or corsproxy.io so those cross-origin files can be read.</li>
          <li><strong>Dictate:</strong> uses the browser's Web Speech API. Speech processing can be handled by the browser or operating-system vendor and may require a network connection.</li>
        </ul>
        <p>
          Do not submit secrets, authenticated URLs, private network addresses,
          or content you are not permitted to share to a network-assisted tool.
        </p>
      </InfoSection>

      <InfoSection title="Files, retention, and control">
        <p>
          Temporary previews and converted results are held by the current
          browser page until they are downloaded, removed, refreshed, or the tab
          is closed. No Gatekeeping does not provide user accounts, cloud file
          storage, or a server-side transcript archive.
        </p>
        <p>
          Clipboard access occurs only after a copy action. Download actions
          create a file for the browser to save. You can remove site-specific
          local data through your browser's storage settings and can block
          analytics or third-party assets with your browser's privacy controls.
        </p>
      </InfoSection>

      <InfoSection title="Questions and updates">
        <p>
          This page reflects the current implementation and was reviewed on
          July 16, 2026. When a tool's processing path changes, its on-page data
          handling note and this summary should change with it.
        </p>
        <p>
          Report a reproducible discrepancy in the
          {" "}
          <a
            href="https://github.com/semifrontier/ng-app-new/issues"
            rel="noreferrer"
            target="_blank"
          >
            public issue tracker
          </a>
          , or read <Link to="/about">how the site reviews product and editorial claims</Link>.
        </p>
      </InfoSection>
    </InfoPageShell>
  );
}
